import React from 'react';
import { Box, Button, IconButton, InputBase, MenuItem, Select, Tooltip } from '@mui/material';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { PR_PACKAGES } from 'src/utils/collections/companyLeads';

/**
 * Status configuration for dashboard status column
 * Maps status keys to display labels and colors
 */
export const dashboardStatusConfig = {
  risk: { label: 'At risk', bgcolor: '#FFB8C5', color: '#940000' },
  attention: { label: 'Needs attention', bgcolor: '#FFE37D', color: '#C76102' },
  onTrack: { label: 'On track', bgcolor: '#86FC91', color: '#107015' },
};

/** Shared sx for inline Select dropdowns inside DataGrid cells */
const inlineSelectSx = {
  fontSize: '0.875rem',
  width: '100%',
  '& .MuiSelect-select': {
    py: 0.5,
    px: 1,
  },
  '&:before, &:after': { display: 'none' },
};

/**
 * Get DataGrid columns for the Wins Dashboard
 *
 * All editable fields render real form controls (Select / InputBase) directly
 * in the cell — single-click interactive, no DataGrid edit mode needed.
 *
 * @param {Function} onViewReport - Callback when "View report" button is clicked
 * @param {Object} options
 * @param {Array}    options.users          - All user objects (for display lookups)
 * @param {Array}    options.prTeamUsers    - PR team user objects (for lead dropdown)
 * @param {Function} options.onCellChange   - (companyName, field, value) => void
 * @param {Function} options.onRemoveCompany
 * @param {Function} options.onRestoreCompany
 * @param {boolean}  options.isEditMode
 */
export const getWinsDashboardColumns = (onViewReport, options = {}) => {
  const { users = [], prTeamUsers = [], onCellChange, onRemoveCompany, onRestoreCompany, isEditMode = false } = options;

  return [
    {
      field: 'package',
      headerName: 'Package',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Select
          variant="standard"
          value={params.row.package || ''}
          onChange={(e) => onCellChange?.(params.row.company, 'package', e.target.value)}
          displayEmpty
          renderValue={(v) => v || '----'}
          sx={inlineSelectSx}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {Object.values(PR_PACKAGES).map((pkg) => (
            <MenuItem key={pkg} value={pkg}>
              {pkg}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 2,
      minWidth: 200,
      sortComparator: (a, b) => a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'base' }),
    },
    {
      field: 'primaryLead',
      headerName: 'Primary Lead',
      flex: 1.5,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Select
          variant="standard"
          value={params.row.leadId || ''}
          onChange={(e) => onCellChange?.(params.row.company, 'leadId', e.target.value)}
          displayEmpty
          renderValue={(v) => {
            if (!v) return '----';
            const user = users.find((u) => u.id === v);
            return user?.firstName || '----';
          }}
          sx={inlineSelectSx}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {prTeamUsers.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.firstName || `User ${user.id}`}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'successful',
      headerName: 'Successful',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, width: '100%' }}>
          <span>{params.row.successful}</span>
          <span>/</span>
          <InputBase
            type="number"
            defaultValue={params.row.successfulQuota ?? ''}
            onBlur={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value);
              if (val !== params.row.successfulQuota) {
                onCellChange?.(params.row.company, 'successfulQuota', val);
              }
            }}
            inputProps={{ min: 0, style: { textAlign: 'center', width: 36, padding: '2px 0' } }}
            sx={{
              fontSize: '0.875rem',
              borderBottom: '1px dashed',
              borderColor: 'grey.400',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'submitted',
      headerName: 'Submitted',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, width: '100%' }}>
          <span>{params.row.submitted}</span>
          <span>/</span>
          <InputBase
            type="number"
            defaultValue={params.row.submittedQuota ?? ''}
            onBlur={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value);
              if (val !== params.row.submittedQuota) {
                onCellChange?.(params.row.company, 'submittedQuota', val);
              }
            }}
            inputProps={{ min: 0, style: { textAlign: 'center', width: 36, padding: '2px 0' } }}
            sx={{
              fontSize: '0.875rem',
              borderBottom: '1px dashed',
              borderColor: 'grey.400',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) =>
        !params.row.isUnassigned ? (
          <Button variant="contained" color="primary" size="small" onClick={() => onViewReport(params.row.company)}>
            View report
          </Button>
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const currentStatus = params.row.status || '';
        const config = currentStatus ? dashboardStatusConfig[currentStatus] : null;

        return (
          <Select
            variant="standard"
            value={currentStatus}
            onChange={(e) => onCellChange?.(params.row.company, 'status', e.target.value)}
            displayEmpty
            renderValue={(v) => {
              if (!v) return '----';
              return dashboardStatusConfig[v]?.label || '----';
            }}
            sx={{
              ...inlineSelectSx,
              ...(config && {
                bgcolor: config.bgcolor,
                color: config.color,
                fontWeight: 600,
                borderRadius: 1,
                '& .MuiSelect-select': { py: 0.5, px: 1 },
                '& .MuiSelect-icon': { color: config.color },
              }),
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {Object.entries(dashboardStatusConfig).map(([key, { label, bgcolor, color }]) => (
              <MenuItem key={key} value={key} sx={{ bgcolor, color, fontWeight: 600, '&:hover': { bgcolor, opacity: 0.85 } }}>
                {label}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    ...(isEditMode
      ? [
          {
            field: 'remove',
            headerName: '',
            width: 60,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
              if (params.row.isUnassigned) return null;
              if (params.row.isHidden) {
                return (
                  <Tooltip title="Show company">
                    <IconButton size="small" color="primary" onClick={() => onRestoreCompany?.(params.row)}>
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                );
              }
              return (
                <Tooltip title="Hide company">
                  <IconButton size="small" onClick={() => onRemoveCompany?.(params.row)}>
                    <VisibilityOffOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];
};
